// 3rd party
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Types
import { LeadTimeError, Product } from 'types/parts';

// Constants
import { LONG_LEAD_TIME_THRESHOLD } from 'constants/parts';
import breakpoint from 'constants/breakpoints';

// Theme
import theme from 'themes';

// Helpers
import { getPartialLeadTimeText } from 'common/helpers/v1';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';
import InformationTooltip from 'components/InformationTooltip/InformationTooltip';
import { TextEllipsis } from 'common/components/common-styled';
import Typography from 'components/Typography/Typography';

type AvgLeadTimeCellProps = {
  purchasable: Product | undefined;
};

// Styling
const LeadTimeContainer = styled.div<AvgLeadTimeCellProps>`
  display: flex;
  gap: 0.3rem;
  color: ${(props) => getCanonicalLeadTimeColoring(props.purchasable)};
  justify-content: left;
  align-items: center;
  flex-grow: 1;
  text-align: center;
  width: 10rem;
  white-space: nowrap;
  @media ${breakpoint.device.xxl} {
    text-align: left;
  }
  .icon--triangle-warning {
    width: 1rem;
  }
`;

// only apply the spacing if the icon is going to appear
const LeadTimeIconContainer = styled.div<{
  showIcon: boolean;
}>`
  display: block;
  margin: 0 auto;
  @media ${breakpoint.device.xxl} {
    display: inline-block;
  }
`;
function getCanonicalLeadTimeColoring(purchasable: Product | undefined): string {
  const leadTime = purchasable?.leadTime;
  const inStock = purchasable !== undefined && purchasable.stock > 0;
  if (leadTime !== undefined && leadTime > 0 && inStock) {
    return theme.colors.black;
  }
  return theme.colors.caution200;
}

const AvgLeadTimeCell = ({ purchasable }: AvgLeadTimeCellProps): ReactElement => {
  const { t } = useTranslation(['fpns']);
  const leadTimeValid =
    purchasable?.leadTimeError === undefined || purchasable.leadTimeError === LeadTimeError.NONE;
  const leadTime = purchasable?.leadTime;
  const inStock = purchasable !== undefined && purchasable.stock > 0;
  const showIcon =
    (leadTimeValid && leadTime !== undefined && leadTime > LONG_LEAD_TIME_THRESHOLD) || !inStock;
  return (
    <InformationTooltip
      tooltipText={
        <Typography mb={0} purchasable={purchasable}>
          {getPartialLeadTimeText(purchasable?.leadTime, t)}.&nbsp;
          {(!inStock || leadTime === 0 || leadTime === undefined) && (
            <>{t('call_for_availability', { ns: 'common' })}</>
          )}
        </Typography>
      }
      placement="top"
      icon={
        <LeadTimeContainer purchasable={purchasable}>
          {showIcon && (
            <LeadTimeIconContainer showIcon={showIcon}>
              <TriangleWarning />
            </LeadTimeIconContainer>
          )}
          <TextEllipsis width={'10rem'}>
            {getPartialLeadTimeText(purchasable?.leadTime, t)}.&nbsp;
            {(!inStock || leadTime === 0 || leadTime === undefined) && (
              <>{t('call_for_availability', { ns: 'common' })}</>
            )}
          </TextEllipsis>
        </LeadTimeContainer>
      }
      width="fit-content"
    />
  );
};

export default AvgLeadTimeCell;
